/**
 * Positions header nav slider arrows on the visible slide's .media (image/video),
 * not the full slide including captions below.
 */
class HeaderNavMediaSliderArrows {
  constructor(root) {
    this.root = root;
    this.slider = root.querySelector(':scope > [id^="Slider-"]');
    this._onScroll = () => this.scheduleUpdate();
    this._onResize = () => this.scheduleUpdate();
    this._pending = false;
    this._resizeObserver = null;
    this._mutationObserver = null;
    this._details = root.closest('details');
    this._intersectionObserver = null;
  }

  scheduleUpdate() {
    if (this._pending) return;
    this._pending = true;
    requestAnimationFrame(() => {
      this._pending = false;
      this.update();
    });
  }

  getActiveSlide() {
    if (!this.slider) return null;
    const slides = Array.from(
      this.slider.querySelectorAll(':scope > [id^="Slide-"]')
    ).filter((el) => el.clientWidth > 0);
    if (!slides.length) return null;

    const scrollLeft = this.slider.scrollLeft;
    let best = slides[0];
    let minDelta = Infinity;
    for (const slide of slides) {
      const delta = Math.abs(slide.offsetLeft - scrollLeft);
      if (delta < minDelta) {
        minDelta = delta;
        best = slide;
      }
    }
    return best;
  }

  getAnchorElement() {
    const slide = this.getActiveSlide();
    if (!slide) return null;
    const media = slide.querySelector('.media');
    return media || slide;
  }

  update() {
    if (!this.slider || !this.root.querySelector('.slider-buttons')) return;

    const anchor = this.getAnchorElement();
    if (!anchor) {
      this.root.style.removeProperty('--nav-media-arrow-y');
      return;
    }

    const rootRect = this.root.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const centerY = anchorRect.top + anchorRect.height / 2 - rootRect.top;

    this.root.style.setProperty(
      '--nav-media-arrow-y',
      `${Math.max(0, Math.round(centerY))}px`
    );
  }

  observeMediaLoads() {
    if (!this.slider) return;
    this.slider.querySelectorAll('.media img, .media video').forEach((node) => {
      if (node.tagName === 'IMG' && !node.complete) {
        node.addEventListener('load', () => this.scheduleUpdate(), { once: true });
      } else if (node.tagName === 'VIDEO' && node.readyState < 2) {
        node.addEventListener('loadeddata', () => this.scheduleUpdate(), {
          once: true,
        });
      }
    });
  }

  bind() {
    if (!this.slider) return;

    this.slider.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    if (this._details) {
      this._details.addEventListener('toggle', () => {
        if (this._details.open) {
          requestAnimationFrame(() => {
            this.observeMediaLoads();
            this.update();
          });
        }
      });
    }

    this._intersectionObserver = new IntersectionObserver(
      () => this.scheduleUpdate(),
      { threshold: [0, 0.01, 0.5, 1] }
    );
    this._intersectionObserver.observe(this.root);

    this._resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
    this._resizeObserver.observe(this.root);
    this._resizeObserver.observe(this.slider);

    this._mutationObserver = new MutationObserver(() => {
      this.observeMediaLoads();
      this.scheduleUpdate();
    });
    this._mutationObserver.observe(this.slider, { childList: true, subtree: true });

    this.observeMediaLoads();
    this.scheduleUpdate();
  }

  unbind() {
    if (this.slider) {
      this.slider.removeEventListener('scroll', this._onScroll);
    }
    window.removeEventListener('resize', this._onResize);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._mutationObserver) this._mutationObserver.disconnect();
    if (this._intersectionObserver) this._intersectionObserver.disconnect();
  }
}

function initHeaderNavMediaSliderArrows() {
  document.querySelectorAll('[data-slider-arrows-anchor="media"]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.navMediaArrowsInit === 'true') return;
    el.dataset.navMediaArrowsInit = 'true';
    const instance = new HeaderNavMediaSliderArrows(el);
    instance.bind();
  });
}

document.addEventListener('DOMContentLoaded', initHeaderNavMediaSliderArrows);
document.addEventListener('shopify:section:load', initHeaderNavMediaSliderArrows);
